
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, watch} from "vue";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import InputNumber from '@/components/DexInputNumber.vue';
import Dropdown from "primevue/dropdown";
import {DocumentTableColumn, DocumentLocator, LookupDefinition} from "@dex/squeeze-client-ts";
import {useI18n} from "vue-i18n";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import DbLookupForm from "@/apps/administration/components/documentclasses/DbLookupForm.vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import {
	stringWithNumbersValidation,
	technicalValidation
} from "@/util/ValidationHelper";
import LookupColumnFiltersView from "@/apps/administration/views/squeeze/documentclasses/LookupColumnFiltersView.vue";
import {showDropdownOverlay} from "@/util/StylesHelper";
import AmountSeparatorsForm, {AmountSeparator} from "@/apps/administration/components/documentclasses/AmountSeparatorsForm.vue";
import useSqueezeStore from "@/apps/squeeze/store";

interface DataTypes {
	id: string | undefined;
	text: string;
}

export default defineComponent({
	name: 'TableColumnForm',
	components: {
		AmountSeparatorsForm, Checkbox, InputText, InputNumber, Dropdown, DbLookupForm, TabView, TabPanel, LookupColumnFiltersView,
	},
	props: {
		documentClassId: {
			type: Number,
			default: 0,
		},
		columnEntry: {
			type: Object as PropType<DocumentTableColumn>,
			default: () => ({}),
			required: true,
		},
		locators: {
			type: Array as PropType<DocumentLocator[]>,
			default: () => [],
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
			default: false,
		},
		tableColumns: {
			type: Array as PropType<DocumentTableColumn[]>,
			default: () => [],
		},
	},
	emits: ['update', 'onTabChange'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const store = useSqueezeStore();

		/** Current Object of all input-fields */
		const value = reactive<any>(props.columnEntry);

		/** Determines the required rules for validation */
		const rules = {
			name: { required, technicalValidation },
			description: { required },
			dataType: { required },
			width: { stringWithNumbersValidation },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value, { $scope: false });

		/** List of Data-Types */
		const dataTypes = computed(() => {
			return [
				{
					id: 'Text',
					text: t('Squeeze.General.Text'),
				},
				{
					id: 'Date',
					text: t('Squeeze.General.Date'),
				},
				{
					id: 'Amount',
					text: t('Squeeze.General.Amount'),
				},
			] as DataTypes[];
		});

		/** List of Truncate-Types */
		const truncateTypes = computed(() => {
			return [
				{
					id: DocumentTableColumn.TruncateTypeEnum.None,
					text: t('Squeeze.DocumentClasses.DoNotCutOff'),
				},
				{
					id: DocumentTableColumn.TruncateTypeEnum.Start,
					text: t('Squeeze.DocumentClasses.CutOffStartOfField'),
				},
				{
					id: DocumentTableColumn.TruncateTypeEnum.End,
					text: t('Squeeze.DocumentClasses.CutOffEndOfField'),
				},
			];
		});

		onMounted(async () => {
			Object.assign(value, props.columnEntry);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.columnEntry, async () => {
			Object.assign(value, props.columnEntry);
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		})

		/** Triggered on change tab */
		const onTabChange = (activeTabIndex: number) => {
			emit("onTabChange", activeTabIndex);
		}

		/** Triggered on change of any field */
		const update = async (isInvalidLookup?: boolean) => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			if (isInvalidLookup) {
				emit("update", value, true, 1);
			} else{
				emit("update", value, v$.value.$invalid, 0);
			}
		}

		/** Triggered on change of any field lookup */
		const onUpdateTableLookup = async (lookupTable: LookupDefinition, isInvalid: boolean) => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			Object.assign(value.lookup, lookupTable)
			await update(isInvalid);
		}

		/** Triggered on change of input field */
		const onChangeInput = () => {
			update(false);
		}

		/**
		 * Triggered on change of an amount separator
		 * @param data
		 */
		const onChangeAmountSeparators = (data: AmountSeparator) => {
			value.thousandSeparator = data.thousandSeparator;
			value.decimalSeparator = data.decimalSeparator;
			value.decimalPlaces = data.decimalPlaces;
			update(false);
		}

		/**
		 * Set the lookup to inactive if the Type is not text
		 */
		const onChangeDataType = () => {
			if (value.dataType !== "Text") {
				value.lookup!.active = false;
			}
			// Trigger validation
			update(false);
		}

		/**
		 * Is triggered when a checkbox from mandatory, forceValidation or hidden is clicked
		 * @param fieldName Name of the field
		 */
		const onChangeCheckBox = (fieldName: string) => {
			switch(fieldName) {
			case 'mandatory':
			case 'forceValidation': {
				// If a field is mandatory or "force Validation", it shouldn't be hidden
				if (value.mandatory === true || value.forceValidation === true) {
					value.hidden = false;
				}
				break;
			}
			case 'hidden': {
				// If a field is hidden, forcing the Validation can cause a falsy behavior
				if (value.hidden) {
					value.mandatory = false;
					value.forceValidation = false;
				}
				break;
			}
			}
		}

		return {
			t,
			store,
			dataTypes,
			v$,
			value,
			truncateTypes,
			onTabChange,
			update,
			onUpdateTableLookup,
			onChangeInput,
			onChangeAmountSeparators,
			onChangeDataType,
			onChangeCheckBox,
			showDropdownOverlay,
		}
	},
});
